import { FC, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material'
import SendIcon from '@mui/icons-material/Send'
import { useSnackbar } from 'notistack'
import { PageHeader } from '@/components/common/PageHeader'
import { CurrencyField } from '@/components/common/CurrencyField'
import {
  ChecklistDocumentosReserva,
  obrigatoriosPendentes,
} from '@/components/reservas/ChecklistDocumentosReserva'
import {
  SeletorProduto,
  ehOutraNecessidade,
  produtoOutraNecessidade,
} from '@/components/reservas/SeletorProduto'
import {
  MENSAGEM_ERRO_PRODUTO_RESERVA,
  codigoErroProdutoReserva,
} from '@/components/reservas/errosProdutoReserva'
import { PerguntasCadastraisReserva } from '@/components/reservas/PerguntasCadastraisReserva'
import { SeletorOperacaoAnterior } from '@/components/reservas/SeletorOperacaoAnterior'
import { useAuth } from '@/hooks/useAuth'
import { papelPrincipal } from '@/components/layout/menu'
import { AlertaElegibilidade } from '@/components/reservas/AlertaElegibilidade'
import { SecaoElegibilidadeReserva } from '@/components/reservas/SecaoElegibilidadeReserva'
import { CamposImovel, ValoresImovel } from '@/components/elegibilidade/CamposImovel'
import { elegibilidadeReservaService } from '@/services/elegibilidadeReservaService'
import { AvaliacaoElegibilidadeProduto, DadosDeclaradosElegibilidade } from '@/types/elegibilidade'
import { cnpjService, cnpjValido, mascararCnpj } from '@/services/cnpjService'
import { ConsultaCnpj } from '@/types/cnpj'
import { elegibilidadeService, produtoService } from '@/services/produtoService'
import { MotivoElegibilidade, PerguntaElegibilidade, Produto } from '@/types/produto'
import { oportunidadeService } from '@/services/oportunidadeService'
import { CriarOportunidadeRequest, Oportunidade, RespostaElegibilidadeRequest } from '@/types/oportunidade'
import { estruturacaoConsultorService } from '@/services/estruturacaoConsultorService'
import { ChecklistReserva } from '@/types/estruturacao'
import type { ItemBloqueanteChecklist } from '@/types/checklist'
import { itensBloqueantesDoErro } from '@/services/checklistReservaService'
import { ROTULOS_STATUS_ITEM } from '@/constants/statusChecklist'
import { FormularioDinamico } from '@/components/formulario/FormularioDinamico'
import { pendenciasAte, useFormularioReserva } from '@/hooks/useFormularioReserva'
import { mensagemErroFormulario, pendenciasDoErro } from '@/services/formularioService'
import { PendenciaFormulario } from '@/types/formulario'
import { formatDateTime } from '@/utils/formatters'
import { normalizarCnpj } from '@/utils/cnpj'

/**
 * Nova oportunidade em 3 etapas (revisão da jornada do consultor, 06/09):
 *
 *  1. CNPJ — consulta, confirmação dos dados e faturamento informado;
 *  2. Produto — catálogo (ou "Outra necessidade") e valor estimado, com os chips e o painel de
 *     elegibilidade DEVOLVIDOS PELO SERVIDOR (P2, AN-PP2 §8: `POST /v1/oportunidades/
 *     elegibilidade/avaliar`). Elegibilidade é ALERTA (Q66): nada aqui impede o cadastro — o
 *     impedimento existe só no Encaminhamento ao parceiro, no servidor. `Salvar e continuar` cria a
 *     reserva em RASCUNHO (`POST /v1/oportunidades`) com os dados declarados (`dadosElegibilidade`);
 *  3. Documentos — os dados solicitados pelo produto (formulário dinâmico de CADASTRO, L5/L6),
 *     as perguntas cadastrais do produto (11/09: saíram da seleção do produto —
 *     o negócio pediu que fossem na mesma jornada da inclusão dos documentos) e o checklist
 *     com upload item a item (obrigatórios e opcionais). `Enviar para aprovação` só habilita
 *     sem obrigatório pendente e sem pergunta obrigatória em branco; avalia as respostas no
 *     motor, grava-as na reserva (`PUT /{id}/respostas-elegibilidade`) e faz RASCUNHO → ENVIADA
 *     ("Aguardando aprovação"); `Concluir depois` guarda as respostas dadas e deixa a reserva em
 *     rascunho, com os uploads pendentes disponíveis em Minhas Reservas.
 *
 * O agendamento da reunião SAIU deste fluxo: acontece na Agenda ou no detalhe da
 * reserva, depois. O bloqueio RASCUNHO → ENVIADA por checklist continua no servidor.
 */

const ETAPAS = ['CNPJ', 'Produto', 'Documentos']

/** Mensagens em português para os códigos de erro do backend — sem código bruto. */
const MENSAGEM_ERRO: Record<string, string> = {
  RESERVA_DUPLICADA:
    'Este CNPJ já possui uma reserva ativa para este produto e não está disponível ' +
    'para uma nova indicação neste momento. Nada foi criado.',
  CNPJ_INVALIDO: 'O CNPJ informado é inválido — confira os dígitos.',
  EMPRESA_OBRIGATORIA: 'Informe o CNPJ da empresa para criar a reserva.',
  // P1 (AN-PP1 §3.2): "Outra necessidade" sem o texto livre.
  DESCRICAO_NECESSIDADE_OBRIGATORIA:
    'Descreva a necessidade do cliente para criar a reserva em "Outra necessidade". Nada foi criado.',
  // Onda 5 (AN-11 §2.2): o servidor recusa produto inativo/desconhecido na criação.
  ...MENSAGEM_ERRO_PRODUTO_RESERVA,
}

const codigoDoErro = (e: unknown): string | undefined =>
  (e as { data?: { errorCode?: string } } | null | undefined)?.data?.errorCode

const mensagemDoErro = (e: unknown, fallback: string): string => {
  const codigo = codigoDoErro(e)
  if (codigo && MENSAGEM_ERRO[codigo]) return MENSAGEM_ERRO[codigo]
  const msg = (e as { message?: unknown } | null | undefined)?.message
  return typeof msg === 'string' && msg ? msg : fallback
}

/**
 * "Registrar reserva" em nome de um consultor (Onda 6 / A11): o back-office
 * (ADMIN/GERENTE) abre esta mesma página com `modoAdmin` + o consultor escolhido; o
 * `POST /v1/oportunidades` leva `consultorId`/`consultorNome`, o backend faz a reserva
 * nascer do consultor e carimba a auditoria em nome de quem registrou (403 para
 * qualquer outro perfil que mande `consultorId`).
 */
export interface NovaOportunidadePageProps {
  modoAdmin?: boolean
  consultorId?: string | null
  consultorNome?: string | null
}

const NovaOportunidadePage: FC<NovaOportunidadePageProps> = ({
  modoAdmin = false,
  consultorId = null,
  consultorNome = null,
}) => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { enqueueSnackbar } = useSnackbar()
  const { usuario } = useAuth()
  const papel = papelPrincipal(usuario?.permissoes)

  const [etapa, setEtapa] = useState(0)

  // ── Etapa 1: CNPJ ──
  // Links antigos (?cnpj=..., ex.: retorno do cadastro de cliente) pré-preenchem.
  const [cnpj, setCnpj] = useState(() => mascararCnpj(searchParams.get('cnpj') ?? ''))
  const [consultando, setConsultando] = useState(false)
  const [consulta, setConsulta] = useState<ConsultaCnpj | null>(null)
  const [erroConsulta, setErroConsulta] = useState<string | null>(null)
  const [razaoSocial, setRazaoSocial] = useState('')
  const [nomeFantasia, setNomeFantasia] = useState('')
  const [setor, setSetor] = useState('')
  /** "Faturamento informado" do Figma — sempre manual, mesmo com consulta ok. */
  const [faturamento, setFaturamento] = useState(0)

  // ── Etapa 2: produto + elegibilidade + valor ──
  const [produtos, setProdutos] = useState<Produto[]>([])
  const [carregandoProdutos, setCarregandoProdutos] = useState(true)
  const [erroProdutos, setErroProdutos] = useState(false)
  const [produtoId, setProdutoId] = useState('')
  const [outraNecessidade, setOutraNecessidade] = useState('')
  /** P5 (AN-PP5 §3.6): "Nova operação (renovação de #N)" — só vínculo, nada herdado. */
  const [operacaoAnteriorId, setOperacaoAnteriorId] = useState<string | null>(null)
  const [perguntas, setPerguntas] = useState<PerguntaElegibilidade[]>([])
  const [respostas, setRespostas] = useState<Record<string, string>>({})
  const [valor, setValor] = useState(0)
  /**
   * P2: dados declarados do Home Equity (`LTV_MAX`). "Valor solicitado" nasce com o valor da
   * operação e segue com ele até o consultor editá-lo.
   */
  const [imovel, setImovel] = useState<ValoresImovel>({
    valorImovel: 0,
    valorImovelFonte: 'ESTIMATIVA_CONSULTOR',
    valorSolicitado: 0,
  })
  const [solicitadoEditado, setSolicitadoEditado] = useState(false)
  /** P2: avaliação do servidor para todos os produtos (chips); `null` = sem avaliação (rede/carregando). */
  const [avaliacoes, setAvaliacoes] = useState<AvaliacaoElegibilidadeProduto[] | null>(null)
  const [criando, setCriando] = useState(false)
  const [erroCriar, setErroCriar] = useState<string | null>(null)
  /** Recusa 422 do produto na criação (Onda 5) — mostrada no seletor, que volta à busca. */
  const [avisoProduto, setAvisoProduto] = useState<string | null>(null)

  // ── Etapa 3: documentos da reserva recém-criada (RASCUNHO) ──
  const [reserva, setReserva] = useState<Oportunidade | null>(null)
  const [checklist, setChecklist] = useState<ChecklistReserva | null>(null)
  /** Nome do produto quando a reserva foi retomada (não há `produtoSelecionado` na retomada). */
  const [produtoRetomado, setProdutoRetomado] = useState('')
  // Retomada de rascunho (10/09): `?continuar=<id>` abre direto a etapa 3. "Concluir depois"
  // deixava a reserva sem caminho de volta — a lista só oferecia o diálogo de upload.
  const continuarId = searchParams.get('continuar')
  const [retomando, setRetomando] = useState(Boolean(continuarId))
  const [enviando, setEnviando] = useState(false)
  const [erroEnviar, setErroEnviar] = useState<string | null>(null)
  /** Motivos do motor ligados às perguntas (bloqueio/pendência), mostrados na etapa 3. */
  const [motivosRespostas, setMotivosRespostas] = useState<MotivoElegibilidade[]>([])
  // Nenhum bloco fixo de dados (L6): o que o produto pede vem do formulário dinâmico de
  // CADASTRO (versão fixada na criação da reserva). Produto sem formulário = nada aparece.
  const formCadastro = useFormularioReserva(reserva?.id ?? null, 'CADASTRO')
  /** Pendências do 422 `FORMULARIO_INCOMPLETO` da transição (a lista do servidor). */
  const [pendenciasRecusa, setPendenciasRecusa] = useState<PendenciaFormulario[]>([])
  /** P3: 422 `DOCUMENTOS_OBRIGATORIOS_FALTANDO` — itens do checklist que o backend devolveu. */
  const [itensBloqueantes, setItensBloqueantes] = useState<ItemBloqueanteChecklist[]>([])

  // CNPJ normalizado (sem máscara, maiúsculas) — numérico ou alfanumérico.
  const digitos = normalizarCnpj(cnpj)
  // P1 (AN-PP1 §3.2): "Outra necessidade" é o produto `OTHER_NEED` do catálogo (o seletor
  // devolve o id dele); a chave interna `OUTRA_NECESSIDADE` só sobra se a listagem não o trouxe.
  const idOutraNecessidade = produtoOutraNecessidade(produtos)?.id ?? null
  const outra = ehOutraNecessidade(produtoId, produtos)

  const carregarProdutos = async () => {
    setCarregandoProdutos(true)
    try {
      // L12: o seletor só oferece produto ativo (inativo não pode ser reservado).
      const pg = await produtoService.listar(0, 200, { apenasAtivos: true })
      setProdutos(pg.content ?? [])
      setErroProdutos(false)
    } catch {
      // Falha é ERRO VISÍVEL com retry, não catálogo vazio — sem catálogo não há
      // produto para reservar (a mesma regra do modal antigo).
      setErroProdutos(true)
    } finally {
      setCarregandoProdutos(false)
    }
  }

  useEffect(() => {
    void carregarProdutos()
    // `?cnpj=` (retorno do cadastro de cliente, Onda 8 / A16): além de pré-preencher,
    // dispara a consulta da etapa 1 — o consultor não precisa clicar de novo no que
    // acabou de digitar. Só com CNPJ válido; inválido fica no campo para correção.
    const cnpjInicial = normalizarCnpj(searchParams.get('cnpj'))
    if (cnpjInicial && cnpjValido(cnpjInicial)) void consultar()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleCnpjChange = (valorDigitado: string) => {
    setCnpj(mascararCnpj(valorDigitado))
    // Mudou o CNPJ: a consulta anterior (e os dados confirmados) não valem mais.
    setConsulta(null)
    setErroConsulta(null)
    setRazaoSocial('')
    setNomeFantasia('')
    setSetor('')
  }

  const consultar = async () => {
    setConsultando(true)
    setErroConsulta(null)
    try {
      const r = await cnpjService.consultar(digitos)
      setConsulta(r)
      if (r.disponivel) {
        setRazaoSocial(r.razaoSocial ?? '')
        setNomeFantasia(r.nomeFantasia ?? '')
        setSetor(r.setor ?? '')
      }
    } catch (e) {
      if (codigoDoErro(e) === 'CNPJ_INVALIDO') {
        // Dígito errado se corrige, não se contorna — sem fallback manual aqui.
        setErroConsulta(MENSAGEM_ERRO.CNPJ_INVALIDO)
      } else {
        // O backend nunca responde 5xx para provedor fora (fail-open controlado);
        // se a falha foi ANTES dele (rede/gateway), o espírito é o mesmo:
        // preenchimento manual autorizado.
        setConsulta({ cnpj: digitos, disponivel: false, motivo: 'PROVEDOR_INDISPONIVEL' })
      }
    } finally {
      setConsultando(false)
    }
  }

  /**
   * Trocar de produto invalida as respostas: as perguntas são de OUTRO produto.
   * "Outra necessidade" não carrega perguntas — sem elegibilidade (TBD do Figma).
   */
  const selecionarProduto = async (id: string) => {
    setProdutoId(id)
    // A operação anterior é do MESMO produto: trocar de produto desfaz o vínculo.
    setOperacaoAnteriorId(null)
    setRespostas({})
    setPerguntas([])
    setErroCriar(null)
    if (id) setAvisoProduto(null)
    // "Outra necessidade" não tem elegibilidade (PDF §8): nem a chave interna nem o OTHER_NEED.
    if (!id || ehOutraNecessidade(id, produtos)) return
    try {
      setPerguntas(await elegibilidadeService.perguntas(id))
    } catch {
      // Produto sem perguntas ou falha de rede: segue por valor/prazo — bloquear
      // aqui inventaria uma recusa que ninguém cadastrou.
      setPerguntas([])
    }
  }

  const responder = (perguntaId: string, resposta: string) => {
    setRespostas((prev) => ({ ...prev, [perguntaId]: resposta }))
    setMotivosRespostas([])
    setErroEnviar(null)
  }

  /** Motivos do motor de PERGUNTAS que dependem de uma pergunta (id presente). */
  const daPergunta = (m: MotivoElegibilidade) => Boolean(m.perguntaId)

  // Faturamento anual é obrigatório (especificação de elegibilidade, 07/09): é dado declarado
  // da empresa (Q69) e base da elegibilidade dos produtos de faturamento mínimo.
  const podeContinuar = consulta !== null && razaoSocial.trim().length > 0 && faturamento > 0
  const produtoSelecionado = useMemo(() => produtos.find((p) => p.id === produtoId), [produtos, produtoId])
  /** Avaliação do SERVIDOR para o produto escolhido (painel `AlertaElegibilidade`). */
  const avaliacaoSelecionada = useMemo(
    () => (produtoId ? avaliacoes?.find((a) => a.produtoId === produtoId) ?? null : null),
    [avaliacoes, produtoId]
  )
  /**
   * Produto de política `LTV_MAX` (Home Equity) pede imóvel, fonte e valor solicitado. O TIPO vem
   * do servidor (avaliação ou `ProdutoResponse.elegibilidade`); o campo antigo `ltvMaximoPercentual`
   * (preenchido pelo backend a partir da vigente) só cobre o backend anterior à P2.
   */
  const tipoPolitica = avaliacaoSelecionada?.politicaTipo ?? produtoSelecionado?.elegibilidade?.tipo ?? null
  const exigeImovel =
    !outra && (tipoPolitica === 'LTV_MAX' || (tipoPolitica == null && produtoSelecionado?.ltvMaximoPercentual != null))
  // Sem portão de elegibilidade (Q66): só o que a reserva precisa para existir.
  const podeCriar = useMemo(
    () => (outra ? outraNecessidade.trim().length > 0 : produtoId !== '') && valor > 0,
    [outra, outraNecessidade, produtoId, valor]
  )

  // "Valor solicitado" acompanha o valor da operação até o consultor editá-lo.
  useEffect(() => {
    if (!solicitadoEditado) setImovel((prev) => ({ ...prev, valorSolicitado: valor }))
  }, [valor, solicitadoEditado])

  /**
   * P2 (AN-PP2 §8): chips do seletor e painel do produto vêm de UMA chamada sem `produtoId`,
   * com debounce de 400 ms nas entradas. Falha de rede = sem chip (nunca bloqueia).
   */
  useEffect(() => {
    if (etapa !== 1) return
    let ativo = true
    const t = setTimeout(() => {
      elegibilidadeReservaService
        .avaliar({
          faturamentoAnual: faturamento > 0 ? faturamento : null,
          valorOperacao: valor > 0 ? valor : null,
          valorImovel: exigeImovel && imovel.valorImovel > 0 ? imovel.valorImovel : null,
          valorSolicitado: exigeImovel && imovel.valorSolicitado > 0 ? imovel.valorSolicitado : null,
        })
        .then((r) => {
          if (ativo) setAvaliacoes(r)
        })
        .catch(() => {
          if (ativo) setAvaliacoes(null)
        })
    }, 400)
    return () => {
      ativo = false
      clearTimeout(t)
    }
  }, [etapa, faturamento, valor, exigeImovel, imovel.valorImovel, imovel.valorSolicitado])

  /** Dados declarados enviados na criação (Q69) — o servidor carimba origem, data e autor. */
  const dadosDeclarados = (): DadosDeclaradosElegibilidade => ({
    faturamentoAnual: faturamento > 0 ? faturamento : null,
    ...(exigeImovel
      ? {
          valorImovel: imovel.valorImovel > 0 ? imovel.valorImovel : null,
          valorImovelFonte: imovel.valorImovel > 0 ? imovel.valorImovelFonte : null,
          valorSolicitado: imovel.valorSolicitado > 0 ? imovel.valorSolicitado : null,
        }
      : {}),
  })

  /** Etapa 2 → 3: cria a reserva em RASCUNHO. Nada de reunião aqui. */
  const criar = async () => {
    if (!podeCriar) return
    setCriando(true)
    setErroCriar(null)
    try {
      // P2 (AN-PP2 §8, Q66): SEM portão de elegibilidade — o alerta não impede o cadastro; o
      // servidor avalia, grava o snapshot e só impede o Encaminhamento ao parceiro.
      // Faturamento deixou as notas: vai estruturado em `dadosElegibilidade` (Q69).
      const notas = [
        setor.trim() ? `Setor: ${setor.trim()}` : null,
        consulta?.disponivel
          ? `Dados cadastrais: ${consulta.origem ?? 'consulta'}` +
            (consulta.consultadoEm ? ` em ${formatDateTime(consulta.consultadoEm)}` : '')
          : 'Dados cadastrais informados manualmente (consulta indisponível)',
      ]
        .filter(Boolean)
        .join(' · ')

      const request: CriarOportunidadeRequest = {
        cnpj: digitos,
        razaoSocial: razaoSocial.trim(),
        nomeFantasia: nomeFantasia.trim() || null,
        // "Outra necessidade" vai como texto livre em tipoNecessidade + descricao, com o id
        // do produto OTHER_NEED (AN-PP1 §3.2); sem ele no catálogo (janela do deploy), nulo —
        // o backend normaliza para o OTHER_NEED quando a projeção o conhece.
        tipoNecessidade: outra ? outraNecessidade.trim() : produtoSelecionado?.nome ?? '',
        produtoId: outra ? idOutraNecessidade : produtoId,
        valorEstimado: valor,
        descricao: outra ? outraNecessidade.trim() : null,
        notas,
        dadosElegibilidade: dadosDeclarados(),
        // As respostas de elegibilidade não vão mais na criação (11/09): são dadas na
        // etapa 3 e gravadas por `PUT /{id}/respostas-elegibilidade`.
        // Só em modo admin (A11) — fora dele o campo nem vai no corpo.
        ...(modoAdmin && consultorId ? { consultorId, consultorNome } : {}),
        // P5: só quando escolhida — fora disso o campo nem vai no corpo.
        ...(operacaoAnteriorId && !outra ? { operacaoAnteriorId } : {}),
      }

      const criada = await oportunidadeService.criarReserva(request)
      if (!criada?.id) {
        // Não deveria acontecer; sem id não há como subir documentos.
        enqueueSnackbar('Reserva salva como rascunho.', { variant: 'success' })
        navigate(modoAdmin ? '/reservas-curadoria' : '/reservas')
        return
      }
      setReserva(criada)
      enqueueSnackbar('Reserva salva como rascunho — agora envie os documentos.', {
        variant: 'success',
      })
      setEtapa(2)
    } catch (e) {
      // Onda 5: produto inativado depois que a lista carregou (ou desconhecido, com o
      // interruptor do servidor ligado) — a mensagem vai para o seletor, a escolha é limpa e
      // a lista de ativos recarregada para o produto sumir das opções.
      const codigoProduto = codigoErroProdutoReserva(e)
      if (codigoProduto) {
        void selecionarProduto('')
        setAvisoProduto(MENSAGEM_ERRO_PRODUTO_RESERVA[codigoProduto])
        void carregarProdutos()
        return
      }
      // O backend garante: em qualquer erro (duplicidade etc.), NADA foi criado —
      // a mensagem mapeada diz isso ao consultor.
      setErroCriar(mensagemDoErro(e, 'Não foi possível criar a reserva.'))
    } finally {
      setCriando(false)
    }
  }

  const pendentes = useMemo(() => obrigatoriosPendentes(checklist), [checklist])
  /** Perguntas que bloqueiam (padrão) e ainda estão em branco — seguram o envio como um documento obrigatório. */
  const perguntasSemResposta = useMemo(
    () => perguntas.filter((p) => p.bloqueia !== false && !(respostas[p.id] ?? '').trim()),
    [perguntas, respostas]
  )
  /**
   * O que falta no formulário para o envio, SEGUNDO O SERVIDOR (última resposta do GET/PUT).
   * Com edição local não gravada as pendências estão velhas: o envio grava antes e decide
   * pela resposta nova — a tela nunca calcula obrigatoriedade.
   */
  const pendenciasFormulario =
    formCadastro.temFormulario && !formCadastro.sujo
      ? pendenciasAte(formCadastro.formulario?.pendencias, 'ENVIO_ANALISE')
      : []
  const podeEnviar =
    reserva !== null &&
    checklist !== null &&
    pendentes.length === 0 &&
    perguntasSemResposta.length === 0 &&
    formCadastro.estado !== 'carregando' &&
    pendenciasFormulario.length === 0
  /** Produto da reserva na etapa 3: o escolhido agora ou o da reserva retomada. */
  const produtoDaReserva = reserva?.produtoId ?? (outra ? null : produtoId || null)

  const montarRespostas = (): RespostaElegibilidadeRequest[] =>
    perguntas
      .filter((p) => (respostas[p.id] ?? '').trim())
      .map((p, i) => ({
        perguntaId: p.id,
        enunciado: p.enunciado,
        tipoResposta: p.tipoResposta,
        resposta: respostas[p.id].trim(),
        ordem: p.ordem ?? i,
      }))

  /** Grava as respostas na reserva (só há o que gravar quando o produto tem perguntas). */
  const salvarRespostas = async () => {
    if (!reserva || perguntas.length === 0) return
    await oportunidadeService.substituirRespostasElegibilidade(reserva.id, montarRespostas())
  }

  const destinoFinal = modoAdmin ? '/reservas-curadoria' : '/reservas'

  useEffect(() => {
    if (!continuarId) return
    let ativo = true
    ;(async () => {
      try {
        const r = await estruturacaoConsultorService.reserva(continuarId)
        if (!ativo) return
        if (r.status !== 'RASCUNHO') {
          enqueueSnackbar('Esta reserva já foi enviada — não há cadastro a concluir.', { variant: 'info' })
          navigate(destinoFinal, { replace: true })
          return
        }
        setCnpj(mascararCnpj(r.cnpj ?? ''))
        setRazaoSocial(r.razaoSocial ?? '')
        setProdutoRetomado(r.tipoNecessidade ?? '')
        setReserva(r as unknown as Oportunidade)
        setEtapa(2)
        // Perguntas cadastrais do produto da reserva e o que já foi respondido — a retomada
        // mostra a etapa 3 inteira, não só os documentos.
        if (r.produtoId) {
          const [lista, gravadas] = await Promise.all([
            elegibilidadeService.perguntas(r.produtoId).catch(() => [] as PerguntaElegibilidade[]),
            oportunidadeService.respostasElegibilidade(continuarId).catch(() => []),
          ])
          if (!ativo) return
          setPerguntas(lista)
          setRespostas(Object.fromEntries(gravadas.map((g) => [g.perguntaId, g.resposta ?? ''])))
        }
      } catch {
        if (!ativo) return
        enqueueSnackbar('Não foi possível abrir a reserva para continuar o cadastro.', { variant: 'error' })
        navigate(destinoFinal, { replace: true })
      } finally {
        if (ativo) setRetomando(false)
      }
    })()
    return () => {
      ativo = false
    }
    // Só na abertura com ?continuar=; os demais são estáveis.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [continuarId])

  /** Etapa 3: RASCUNHO → ENVIADA ("Aguardando aprovação"). O backend revalida o checklist. */
  const enviarParaAprovacao = async () => {
    if (!reserva || !podeEnviar) return
    setEnviando(true)
    setErroEnviar(null)
    setMotivosRespostas([])
    setPendenciasRecusa([])
    setItensBloqueantes([])
    try {
      // Portão das PERGUNTAS cadastrais (regra configurada pelo ADMIN, V3 — continua nesta onda,
      // Q-PP2-1): só contam os motivos ligados a perguntas. Faturamento/LTV são da política
      // versionada (P2) e nunca bloqueiam aqui. Rede fora não bloqueia.
      if (perguntas.length > 0 && produtoDaReserva) {
        try {
          const resultado = await elegibilidadeService.avaliar(produtoDaReserva, { respostas })
          const motivos = [...resultado.bloqueios, ...resultado.pendentes].filter(daPergunta)
          if (motivos.length > 0) {
            setMotivosRespostas(motivos)
            setErroEnviar('Operação não elegível para este produto — revise as respostas cadastrais.')
            return
          }
        } catch {
          /* rede fora não vira recusa */
        }
      }
      await salvarRespostas()
      // Formulário do produto: grava o que foi editado e decide pelas pendências que o
      // servidor devolve (o servidor recusa a transição de novo se faltar algo).
      if (formCadastro.temFormulario && formCadastro.formulario?.editavel && formCadastro.sujo) {
        const salvo = await formCadastro.salvar()
        const faltando = pendenciasAte(salvo?.pendencias, 'ENVIO_ANALISE')
        if (faltando.length > 0) {
          setErroEnviar('Ainda faltam dados solicitados pelo produto.')
          return
        }
      }
      await oportunidadeService.transicionarStatus(reserva.id, {
        novoStatus: 'ENVIADA',
        usuarioId: usuario?.id ?? '',
      })
      enqueueSnackbar('Reserva enviada — aguardando aprovação da Originei.', {
        variant: 'success',
      })
      navigate(destinoFinal)
    } catch (e) {
      // 422 FORMULARIO_INCOMPLETO: a lista do servidor diz o que falta; as pendências do
      // formulário são recarregadas para marcar os campos.
      const recusa = pendenciasDoErro(e)
      if (recusa.length > 0) {
        setPendenciasRecusa(recusa)
        setErroEnviar(mensagemErroFormulario(e, 'Ainda faltam dados solicitados pelo produto.'))
        void formCadastro.recarregar()
        return
      }
      if (codigoDoErro(e)?.startsWith('FORMULARIO_')) {
        setErroEnviar(mensagemErroFormulario(e, 'Não foi possível salvar os dados do produto.'))
        return
      }
      // Mensagem do bloqueio de checklist do backend — é a "mensagem clara do que falta".
      setItensBloqueantes(itensBloqueantesDoErro(e))
      setErroEnviar(mensagemDoErro(e, 'Não foi possível enviar a reserva para aprovação.'))
    } finally {
      setEnviando(false)
    }
  }

  /** Etapa 3: sai deixando a reserva em rascunho — os uploads seguem em Minhas Reservas. */
  const concluirDepois = async () => {
    // Guarda o que já foi respondido para a retomada; falha aqui não prende o consultor.
    // O formulário do produto salva PARCIAL (pendência não impede guardar).
    if (formCadastro.temFormulario && formCadastro.formulario?.editavel && formCadastro.sujo) {
      try {
        await formCadastro.salvar()
      } catch {
        enqueueSnackbar('Os dados do produto não puderam ser guardados — preencha-os de novo ao retomar.', {
          variant: 'warning',
        })
      }
    }
    if (perguntas.length > 0 && montarRespostas().length > 0) {
      try {
        await salvarRespostas()
      } catch {
        enqueueSnackbar('As respostas não puderam ser guardadas — preencha-as de novo ao retomar.', {
          variant: 'warning',
        })
      }
    }
    enqueueSnackbar(
      modoAdmin
        ? 'Reserva mantida em rascunho em nome do consultor. Ela aparece em Reservas e Curadoria como "Rascunho" até ser enviada para aprovação.'
        : 'Reserva mantida em rascunho. Envie os documentos pendentes pela tela de reservas.',
      { variant: 'info' }
    )
    navigate(destinoFinal)
  }

  const camposEmpresa = (
    <Stack spacing={2}>
      <TextField
        label="Razão social"
        value={razaoSocial}
        onChange={(e) => setRazaoSocial(e.target.value)}
        fullWidth
        required
      />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          label="Nome fantasia"
          value={nomeFantasia}
          onChange={(e) => setNomeFantasia(e.target.value)}
          fullWidth
        />
        <TextField
          label="Setor"
          value={setor}
          onChange={(e) => setSetor(e.target.value)}
          fullWidth
        />
      </Stack>
      <CurrencyField
        label="Faturamento anual informado (R$)"
        value={faturamento}
        onChange={setFaturamento}
        fullWidth
        required
        helperText="Declarado pelo consultor (informado pelo cliente) — não vem da consulta. Fica registrado com data e versão."
      />
    </Stack>
  )

  const produtoNome = outra
    ? outraNecessidade.trim()
    : produtos.find((p) => p.id === produtoId)?.nome ?? ''

  return (
    <Box>
      <PageHeader
        title={modoAdmin ? 'Registrar reserva' : 'Nova oportunidade'}
        subtitle={
          modoAdmin
            ? `Reserva registrada pela Originei em nome de ${consultorNome ?? 'consultor'}: a reserva nasce do consultor.`
            : 'Reserve a oportunidade em três etapas: empresa confirmada pelo CNPJ, produto e valor, documentos.'
        }
      />

      {retomando && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress aria-label="Abrindo a reserva para continuar" />
        </Box>
      )}

      {!retomando && (
      <Stepper activeStep={etapa} sx={{ mb: 3, maxWidth: 640 }}>
        {ETAPAS.map((rotulo) => (
          <Step key={rotulo}>
            <StepLabel>{rotulo}</StepLabel>
          </Step>
        ))}
      </Stepper>
      )}

      {!retomando && etapa === 0 && (
        <Paper sx={{ p: 3, maxWidth: 720 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
            1. CNPJ da empresa
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
            <TextField
              label="CNPJ"
              placeholder="00.000.000/0000-00"
              value={cnpj}
              onChange={(e) => handleCnpjChange(e.target.value)}
              fullWidth
              // Sem inputMode numérico: o CNPJ alfanumérico (Receita, 2026) tem letras.
              inputProps={{ autoCapitalize: 'characters', spellCheck: false }}
              error={digitos.length === 14 && !cnpjValido(cnpj)}
              helperText={
                digitos.length === 14 && !cnpjValido(cnpj)
                  ? 'CNPJ inválido: confira os dígitos verificadores.'
                  : 'Numérico ou alfanumérico (letras e números + 2 dígitos verificadores).'
              }
            />
            <Button
              variant="contained"
              onClick={() => void consultar()}
              // Só consulta CNPJ completo e com dígitos verificadores válidos —
              // com menos que isso o backend responderia 400 CNPJ_INVALIDO.
              disabled={!cnpjValido(cnpj) || consultando}
              sx={{ whiteSpace: 'nowrap', minWidth: 170 }}
            >
              {consultando ? 'Consultando…' : 'Consultar CNPJ'}
            </Button>
          </Stack>

          {erroConsulta && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {erroConsulta}
            </Alert>
          )}

          {consulta && consulta.disponivel && (
            <Stack spacing={2}>
              <Alert severity="info">
                Os dados devem ser confirmados antes de criar a reserva.
                {consulta.situacao ? ` Situação cadastral: ${consulta.situacao}.` : ''}
                {consulta.municipio
                  ? ` ${consulta.municipio}${consulta.uf ? `/${consulta.uf}` : ''}.`
                  : ''}
              </Alert>
              {camposEmpresa}
            </Stack>
          )}

          {consulta && !consulta.disponivel && (
            <Stack spacing={2}>
              <Alert severity="warning">
                Consulta indisponível — preenchimento manual autorizado. Informe os
                dados da empresa para continuar.
              </Alert>
              {camposEmpresa}
            </Stack>
          )}

          <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 3 }}>
            <Button onClick={() => navigate('/reservas')}>Cancelar</Button>
            <Button
              variant="contained"
              onClick={() => setEtapa(1)}
              disabled={!podeContinuar}
            >
              Continuar
            </Button>
          </Stack>
        </Paper>
      )}

      {etapa === 1 && (
        <Paper sx={{ p: 3, maxWidth: 860 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {razaoSocial} · CNPJ {mascararCnpj(digitos)}
          </Typography>

          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
            2. Produto
          </Typography>

          {carregandoProdutos ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress aria-label="Carregando catálogo de produtos" />
            </Box>
          ) : erroProdutos ? (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              action={
                <Button color="inherit" size="small" onClick={() => void carregarProdutos()}>
                  Tentar novamente
                </Button>
              }
            >
              Não foi possível carregar o catálogo de produtos. Sem ele a reserva não
              pode ser criada.
            </Alert>
          ) : (
            <>
              {produtos.length === 0 && (
                <Alert severity="info" sx={{ mb: 1 }}>
                  Catálogo de produtos vazio — descreva a necessidade em "Outra
                  necessidade" ou contate a Originei.
                </Alert>
              )}
              {/* VC-13: campo com busca que expande a lista por família (chips de elegibilidade
                  do servidor, P2); escolhido, vira card de resumo com "Trocar produto". */}
              {/* mb: o seletor termina no card "Resumo do produto" e o que vem depois
                  (necessidade, perguntas, valor) não pode encostar nele (09/09). */}
              <Box sx={{ mb: 2 }}>
                <SeletorProduto
                  produtos={produtos}
                  avaliacoes={avaliacoes}
                  value={produtoId}
                  onChange={(id) => void selecionarProduto(id)}
                  aviso={avisoProduto}
                />
              </Box>
              {produtoId && !outra && (
                <SeletorOperacaoAnterior
                  key={produtoId}
                  cnpj={digitos}
                  produtoId={produtoId}
                  consultorId={modoAdmin ? consultorId : null}
                  value={operacaoAnteriorId}
                  onChange={setOperacaoAnteriorId}
                />
              )}
            </>
          )}

          {outra && (
            <TextField
              label="Descreva a necessidade"
              value={outraNecessidade}
              onChange={(e) => setOutraNecessidade(e.target.value)}
              fullWidth
              required
              multiline
              minRows={2}
              sx={{ mb: 2 }}
              helperText="Elegibilidade decidida pela Originei — a Originei avalia a necessidade descrita."
            />
          )}

          {/* Seção própria, com o mesmo título da "2. Produto": antes o campo vinha
              colado ao painel do produto, sem separação visual (09/09). */}
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 3, mb: 0.5 }}>
            Valor da operação
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Estimativa do crédito pretendido. Na Antecipação de recebíveis é ele que a política de
            elegibilidade avalia (faixa e Grandes Operações).
          </Typography>
          <CurrencyField
            label="Valor estimado da operação (R$)"
            value={valor}
            onChange={setValor}
            fullWidth
            required
            sx={{ mb: 2 }}
          />

          {exigeImovel && (
            <Box sx={{ mb: 2 }} component="section" aria-label="Dados do imóvel">
              <CamposImovel
                valores={imovel}
                onChange={(v) => {
                  if (v.valorSolicitado !== imovel.valorSolicitado) setSolicitadoEditado(true)
                  setImovel(v)
                }}
              />
            </Box>
          )}

          {/* P2: painel com as explicações do servidor, a versão da política e o aviso fixo. */}
          {avaliacaoSelecionada && !outra && (
            <AlertaElegibilidade avaliacao={avaliacaoSelecionada} sx={{ mb: 2 }} />
          )}

          <Alert severity="info">
            A reserva é salva como rascunho e, na próxima etapa, você envia os documentos
            do produto. A reunião com o Comercial Originei é agendada depois, pela Agenda.
          </Alert>

          {erroCriar && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {erroCriar}
            </Alert>
          )}

          <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 3 }}>
            <Button onClick={() => setEtapa(0)} disabled={criando}>
              Voltar
            </Button>
            <Button variant="contained" onClick={() => void criar()} disabled={!podeCriar || criando}>
              {criando ? 'Salvando…' : 'Salvar e continuar'}
            </Button>
          </Stack>
        </Paper>
      )}

      {etapa === 2 && reserva && (
        <Paper sx={{ p: 3, maxWidth: 860 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {razaoSocial} · CNPJ {mascararCnpj(digitos)}
              {produtoNome || produtoRetomado ? ` · ${produtoNome || produtoRetomado}` : ''}
            </Typography>
            <Chip size="small" label="Rascunho" />
          </Stack>

          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
            3. Documentos do produto
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Envie os documentos obrigatórios para concluir a reserva. Sem todos os
            obrigatórios, ela fica em rascunho e os uploads seguem disponíveis em Minhas
            Reservas.
          </Typography>

          {/* Dados solicitados pelo produto (formulário CADASTRO, L5/L6): acima das perguntas
              cadastrais. Só existe quando o produto tem formulário publicado — nada fixo. */}
          {formCadastro.estado === 'erro' && (
            <Alert
              severity="warning"
              sx={{ mb: 3 }}
              action={
                <Button color="inherit" size="small" onClick={() => void formCadastro.recarregar()}>
                  Tentar novamente
                </Button>
              }
            >
              Não foi possível carregar os dados solicitados pelo produto. O envio é conferido
              pela Originei mesmo assim.
            </Alert>
          )}
          {formCadastro.temFormulario && formCadastro.formulario?.schema && (
            <Box sx={{ mb: 3 }} component="section" aria-label="Dados solicitados pelo produto">
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                Dados solicitados pelo produto
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Informações que este produto pede no cadastro. Campos com * são exigidos para
                enviar para aprovação; "Concluir depois" guarda o que já foi preenchido.
              </Typography>
              <FormularioDinamico
                schema={formCadastro.formulario.schema}
                respostas={formCadastro.respostas}
                pendencias={pendenciasFormulario}
                somenteLeitura={!formCadastro.formulario.editavel || enviando || formCadastro.salvando}
                onChange={formCadastro.alterar}
              />
            </Box>
          )}

          {/* Perguntas cadastrais do produto (11/09): antes ficavam na seleção do produto;
              o negócio pediu que fossem respondidas na mesma jornada dos documentos. */}
          {perguntas.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                Perguntas cadastrais do produto
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Respostas do cliente às perguntas do produto. Elas ficam gravadas na reserva e
                entram na validação da Originei.
              </Typography>
              <PerguntasCadastraisReserva
                perguntas={perguntas}
                respostas={respostas}
                onChange={responder}
                disabled={enviando}
              />
              {motivosRespostas.length > 0 && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  <AlertTitle>Operação não elegível para este produto</AlertTitle>
                  {motivosRespostas.map((m, i) => (
                    <Typography key={i} variant="body2">
                      • {m.mensagem}
                    </Typography>
                  ))}
                </Alert>
              )}
            </Box>
          )}

          {/* P2 (AN-PP2 §8): cartão "Elegibilidade" com os dados declarados editáveis (PUT …/dados). */}
          <SecaoElegibilidadeReserva oportunidadeId={reserva.id} papel={papel} modo="cadastro" />

          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            Checklist documental
          </Typography>
          <ChecklistDocumentosReserva
            reservaId={reserva.id}
            onChecklist={setChecklist}
            itemDestacado={searchParams.get('item')}
          />

          {checklist &&
            (pendentes.length > 0 || perguntasSemResposta.length > 0 || pendenciasFormulario.length > 0) && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Para enviar para aprovação, ainda faltam:{' '}
              {[
                ...pendentes,
                ...perguntasSemResposta.map((p) => `resposta a "${p.enunciado}"`),
                ...pendenciasFormulario.map((p) => p.mensagem),
              ].join(', ')}.
            </Alert>
          )}

          {erroEnviar && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {erroEnviar}
              {pendenciasRecusa.map((p, i) => (
                <Typography key={i} variant="body2">
                  • {p.mensagem}
                </Typography>
              ))}
              {itensBloqueantes.map((i) => (
                <Typography key={i.chave ?? i.rotulo} variant="body2">
                  • {i.rotulo}
                  {i.status ? ` — ${ROTULOS_STATUS_ITEM[i.status] ?? i.status}` : ''}
                </Typography>
              ))}
            </Alert>
          )}

          <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 3 }}>
            <Button onClick={() => void concluirDepois()} disabled={enviando}>
              Concluir depois
            </Button>
            <Button
              variant="contained"
              onClick={() => void enviarParaAprovacao()}
              disabled={!podeEnviar || enviando}
              startIcon={enviando ? <CircularProgress size={14} color="inherit" /> : <SendIcon />}
            >
              Enviar para aprovação
            </Button>
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Ao enviar, a reserva passa a "Aguardando aprovação" e é validada pelo time
            Originei antes de avançar.
          </Typography>
        </Paper>
      )}
    </Box>
  )
}

export default NovaOportunidadePage
